import {isObject} from '@vueuse/core';
import type {Availability, AvailabilityArticle} from '../interfaces/availability.type';
import type {DeliveryDate} from '../interfaces/delivery.date.type';
import type {BaseOrderLine, OrderLineWithAvailability} from '../interfaces/order.line.type';
import type {BaseProduct, PackingUnitPerCompanyId} from '../interfaces/product.type';
import {getAvailabilityByOrderLine} from './availability.service';
import {isInvalidAmount, isInvalidTotalAmountForVariety} from './order.service';
import {getOrderLinesForDeliveryDate, shoppingCartStorage} from './shoppingcart.storage.service';

/**
 * Convert a Product to a BaseOrderLine with the given amount, week, year and region.
 *
 * @param week Delivery week
 * @param year Delivery year
 * @param region Product region
 * @param product Product to order
 * @param newAmount Amount to order
 */
export const createOrderLine = (week: number, year: number, region: string, product: BaseProduct, newAmount: number): BaseOrderLine => {
	return {
		amount: newAmount,
		articleId: product.patcode,
		consignment: null,
		formId: product.form?.patId,
		formName: product.form?.shortName,
		groupId: '',
		groupName: product.productgroup,
		locationId: product.location?.shortName || null,
		image: product.image,
		notExistingVariety: false,
		region: region,
		preferredDeliveryDay: null,
		packingUnit: getPackingUnit(product.packingUnitPerCompanyId),
		urlFriendlyId: product.urlFriendlyId,
		urlFriendlyName: product.urlFriendlyName,
		urlFriendlyProductgroup: product.urlFriendlyProductgroup,
		articleName: product.name,
		week,
		year
	};
}

export const roundUpAmount = (amount: number, packingUnit: number): number => Math.ceil(amount / packingUnit) * packingUnit;

/**
 * Rounds the given amount up to the nearest multiple of the given packing unit,
 * but never higher than the available amount.
 *
 * @param amount The amount to round up.
 * @param available The available amount.
 * @param packingUnit The packing unit.
 */
export const getRoundedValue = (amount: number, available: number, packingUnit: number): number => {
	const roundedUpAmount = roundUpAmount(amount, packingUnit);

	// The rounded up amount exceeds the actual availability for that product.
	if (roundedUpAmount > available) {
		// Calculate the first nearest rounded up amount for the actual availability
		const firstNearestHighestForAvailability = roundUpAmount(available, packingUnit);
		// Calculate the first nearest rounded down amount for the actual availability
		const firstNearestLowestForAvailability = firstNearestHighestForAvailability - packingUnit;

		// If the first nearest rounded up amount is exactly the same as the availability we will use that.
		return firstNearestHighestForAvailability === available ? available : firstNearestLowestForAvailability;
	}

	return roundedUpAmount;
};

/**
 * Get the packing unit for the given product.
 * Returns 100 if the packing unit is unknown.
 *
 * @param packingUnitPerCompanyId The product packing unit per companyId.
 */
export const getPackingUnit = (packingUnitPerCompanyId: PackingUnitPerCompanyId) => {
	if (!packingUnitPerCompanyId) {
		return 100;
	}

	const companyId = window.userCompanyId;

	// Get the correct packingUnit for the companyId.
	// Fallback to the default 100 whenever there is no specific packingUnit for a company.
	return packingUnitPerCompanyId[companyId] ?? 100;
};

export const orderLineWithState = (orderLine: BaseOrderLine, availabilities: AvailabilityArticle[], amountAvailableFallback?: number) => {
	const availability: Availability | undefined = getAvailabilityByOrderLine(orderLine, availabilities);

	if (!availability) {
		return orderLine as OrderLineWithAvailability;
	}

	let amountAvailable: number = 0;

	if (availability && isObject(availability)) {
		amountAvailable = availability.amount;
	}

	if (amountAvailableFallback && !availability) {
		amountAvailable = amountAvailableFallback;
	}

	const orderLineWithState: OrderLineWithAvailability = {} as OrderLineWithAvailability;

	Object.assign(orderLineWithState, orderLine);

	Object.assign(orderLineWithState, {
		amountAvailable: amountAvailable,
		invalidTotalAmountForVariety: isInvalidTotalAmountForVariety(orderLine, shoppingCartStorage.value.orderLines, amountAvailable),
		invalid: isInvalidAmount(orderLine.amount, orderLine.packingUnit, amountAvailable)
	});

	return orderLineWithState;
};

export const orderLinesWithState = (orderLines: BaseOrderLine[], availabilities: AvailabilityArticle[]): OrderLineWithAvailability[] => {
	if (!orderLines || orderLines.length < 1) {
		return [];
	}

	return orderLines.map((orderLine: BaseOrderLine) => orderLineWithState(orderLine, availabilities));
};

export const hasInvalidOrderLines = (deliveryDate: DeliveryDate, availabilities: AvailabilityArticle[]) => {
	const orderLines = getOrderLinesForDeliveryDate(deliveryDate.week, deliveryDate.year);
	return orderLinesWithState(orderLines, availabilities)
		.some((orderLine: OrderLineWithAvailability) => orderLine.invalid || orderLine.invalidTotalAmountForVariety);
};

export const compareOrderLineByGroupAndArticleName = (line1: BaseOrderLine, line2: BaseOrderLine) => {
	return line1.groupName.localeCompare(line2.groupName) || line1.articleName.localeCompare(line2.articleName);
};

export const isSameVarietyAndWeek = (orderLine: BaseOrderLine, other: BaseOrderLine) => {
	return orderLine && other &&
		orderLine.articleId === other.articleId &&
		orderLine.formId === other.formId &&
		orderLine.week === other.week &&
		orderLine.year === other.year;
};

export const isSameConsignment = (orderLine: BaseOrderLine, other: BaseOrderLine) => {
	return (!orderLine.consignment && !other.consignment)
		|| (orderLine.consignment === other.consignment);
};

export const isUnrootedCutting = (formId: string): boolean => {
	return ['001', '002', '003', '004', '005', '006', '007', '008', '009'].includes(formId);
};
